import { useMemo, useState } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material"
import InfoIcon from "@mui/icons-material/Info"
import CloseIcon from "@mui/icons-material/Close"
import {
  addDays,
  endOfWeekSunday,
  formatDMY,
  getFixedWeekStartsForMonth,
  getISOWeekNumber,
  isoDayFromDate,
  startOfDayLocal,
  startOfWeekMonday,
  strongestFeelingOfDay,
} from "./utils/helpers"
import { DailyEntry } from "./types/types"
import { FeelingModel, FeelingTargetModel } from "services/openapi"
import { useTranslation } from "react-i18next"

type ViewMode = "year" | "month" | "week"

export interface FeelingsCalendarProps {
  data: DailyEntry[]
  labels: {
    options: {
      [key: number]: FeelingModel
    }
    targets: {
      [key: number]: FeelingTargetModel
    }
  }

  /** Which year/month to show (defaults to current local year/month) */
  year?: number
  month?: number // 0-11

  /** Start view */
  view?: ViewMode

  /**
   * Return the URL for the SVG representing a feelingId.
   * Example: (id) => `/assets/feelings/${id}.svg`
   */
  getSvgUrl: (feelingId: number) => string

  /**
   * Optional: called when a day is clicked
   */
  onDayClick?: (day: { isoDay: string; entry?: DailyEntry; strongestFeelingId?: number }) => void

  /**
   * Cell size in px (calendar will scroll if needed)
   */
  cellSize?: number
}

type WeekCellDetails = {
  weekStart: Date
  weekEnd: Date
  strongestOfWeek?: { feelingId: number; strength: number; isoDay: string }
  daysWithEntries: number
  perDayStrongest: Array<{ isoDay: string; feelingId: number; strength: number; feelingTargetIds: number[] }>
  topFeelings: Array<{ feelingId: number; sum: number; total: number }>
}

function computeWeekCellDetails(byDay: Map<string, DailyEntry>, weekStart: Date): WeekCellDetails {
  const weekEnd = addDays(weekStart, 6)

  // Aggregate across ALL feelings in the 7-day span (even if spanning months)
  const agg = new Map<number, { sum: number; total: number }>()

  // Track a representative day for each feeling (highest single strength in the week)
  const bestOccurrence = new Map<number, { isoDay: string; strength: number }>()

  let strongestOfWeek: WeekCellDetails["strongestOfWeek"] = undefined
  const perDayStrongest: WeekCellDetails["perDayStrongest"] = []
  let daysWithEntries = 0

  for (let i = 0; i < 7; i++) {
    const d = addDays(weekStart, i)
    const iso = isoDayFromDate(d)
    const entry = byDay.get(iso)
    if (!entry) continue

    daysWithEntries++

    // per-day strongest (for tooltip)
    const dayStrongest = strongestFeelingOfDay(entry)
    if (dayStrongest) {
      perDayStrongest.push({
        isoDay: iso,
        feelingId: dayStrongest.feelingId,
        strength: dayStrongest.strength,
        feelingTargetIds: dayStrongest.feelingTargetIds,
      })
    }

    // full-week aggregates (sum/total) across all feelings in the entry
    for (const f of entry.feelings ?? []) {
      const cur = agg.get(f.feelingId) ?? { sum: 0, total: 0 }
      cur.sum += f.strength
      cur.total += 1
      agg.set(f.feelingId, cur)

      const prevOcc = bestOccurrence.get(f.feelingId)
      if (!prevOcc || f.strength > prevOcc.strength || (f.strength === prevOcc.strength && iso < prevOcc.isoDay)) {
        bestOccurrence.set(f.feelingId, { isoDay: iso, strength: f.strength })
      }
    }
  }

  const topFeelings = Array.from(agg.entries())
    .map(([feelingId, v]) => ({ feelingId, sum: v.sum, total: v.total }))
    .sort((a, b) => (b.sum !== a.sum ? b.sum - a.sum : a.feelingId - b.feelingId))
    .slice(0, 5) // keep tooltip compact

  // Strongest-of-week = feeling with highest SUM of strengths during the week
  const strongestAgg = Array.from(agg.entries()).reduce<{ feelingId: number; sum: number; total: number } | undefined>(
    (best, [feelingId, v]) => {
      if (!best) return { feelingId, sum: v.sum, total: v.total }
      if (v.sum !== best.sum) return v.sum > best.sum ? { feelingId, sum: v.sum, total: v.total } : best
      if (v.total !== best.total) return v.total > best.total ? { feelingId, sum: v.sum, total: v.total } : best
      return feelingId > best.feelingId ? { feelingId, sum: v.sum, total: v.total } : best
    },
    undefined
  )

  if (strongestAgg) {
    const occ = bestOccurrence.get(strongestAgg.feelingId)
    strongestOfWeek = {
      feelingId: strongestAgg.feelingId,
      strength: strongestAgg.sum, // weekly sum
      isoDay: occ?.isoDay ?? isoDayFromDate(weekStart),
    }
  }

  return {
    weekStart,
    weekEnd,
    strongestOfWeek,
    daysWithEntries,
    perDayStrongest,
    topFeelings,
  }
}

function SvgCell({
  feelingId,
  getSvgUrl,
  size,
}: {
  feelingId?: number
  getSvgUrl: (id: number) => string
  size: number
}) {
  // Render placeholder if no feeling
  if (feelingId == null) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      ></Box>
    )
  }

  const svgUrl = getSvgUrl(feelingId)

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: "background.paper",
        display: "grid",
        placeItems: "center",
        "& svg": {
          width: "100%",
          height: "100%",
          display: "block",
        },
      }}
    >
      {" "}
      <img src={svgUrl} width={28} alt={`${svgUrl}`} aria-hidden="true" />
    </Box>
  )
}

function SvgIcon({ feelingId, getSvgUrl }: { feelingId?: number; getSvgUrl: (id: number) => string }) {
  if (feelingId == null) {
    return <></>
  }
  const svgUrl = getSvgUrl(feelingId)
  return <img src={svgUrl} width={16} alt={`${svgUrl}`} aria-hidden="true" />
}

// ---- Main Component ----
export function FeelingsCalendar(props: FeelingsCalendarProps) {
  const { data, labels, view, getSvgUrl, onDayClick, cellSize = 32 } = props
  const { options: feelingOptionsById, targets: feelingTargetDetailsById } = labels
  const { t, i18n } = useTranslation()
  const tKey = "feelingsCalendar"
  const formatLocale = i18n.language
  const calendarRowGap = 1.5
  const now = new Date()
  const anchorYear = props.year ?? now.getFullYear()
  const anchorMonth0 = props.month ?? now.getMonth()
  const anchorMonthDate = new Date(anchorYear, anchorMonth0, 1)

  const [detailsModal, setDetailsModal] = useState<{ open: boolean; title: string; content: JSX.Element }>(() => ({
    open: false,
    title: "",
    content: <Box />,
  }))

  const openDetailsModal = (title: string, content: JSX.Element) => {
    setDetailsModal({ open: true, title, content })
  }

  const closeDetailsModal = () => {
    setDetailsModal((m) => ({ ...m, open: false }))
  }

  // Map entries by YYYY-MM-DD (local)
  const byDay = useMemo(() => {
    const m = new Map<string, DailyEntry>()
    for (const e of data) {
      const d = startOfDayLocal(e.date)
      m.set(isoDayFromDate(d), e)
    }
    return m
  }, [data])

  const optionT = (id: number) => `youth-view.feeling-selection.feeling-options.${feelingOptionsById[id]?.name}`
  const targetT = (id: number) =>
    `youth-view.feeling-selection.feeling-target-options.${feelingTargetDetailsById[id]?.name}`

  // ---- Render: Year view (12 columns = months; within each month column: days) ----
  const renderYearView = () => {
    // Rolling past 12 months (current month at top)
    const months = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(anchorMonthDate)
      d.setMonth(d.getMonth() - i)
      return { year: d.getFullYear(), month0: d.getMonth(), date: d }
    })

    const monthLabels = months.map((m) => {
      const yy = String(m.year % 100).padStart(2, "0")
      const mon = m.date.toLocaleString(formatLocale, { month: "short" })
      return `${mon} ${yy}`
    })

    // Precompute week starts per month (fixed 1/8/15/22/29 blocks)
    const monthWeekStarts = months.map((m) => getFixedWeekStartsForMonth(m.year, m.month0))

    // Visible date range = earliest week start .. latest week end among the rendered blocks
    const allWeekStarts = monthWeekStarts.flat()
    const rangeStart = allWeekStarts.reduce<Date | undefined>((min, d) => {
      if (!min) return d
      return d < min ? d : min
    }, undefined)
    const rangeEnd = allWeekStarts.reduce<Date | undefined>((max, d) => {
      const weekEnd = addDays(d, 6)
      if (!max) return weekEnd
      return weekEnd > max ? weekEnd : max
    }, undefined)

    // Max number of fixed weeks any month has (usually 5)
    const maxWeeks = Math.max(...monthWeekStarts.map((w) => w.length))
    const weekColLabels = Array.from({ length: maxWeeks }, (_, i) => `W${i + 1}`)

    return (
      <Box sx={{ overflowX: "auto", pb: 1 }}>
        {/* Title with current date range */}
        {rangeStart && rangeEnd && (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t(`${tKey}.dateRange`, { start: formatDMY(rangeStart), end: formatDMY(rangeEnd) })}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          {/* Month labels */}
          <Box sx={{ minWidth: 40 }}>
            <Box sx={{ height: 32 }} />
            <Box sx={{ display: "grid", gridTemplateRows: "repeat(12, auto)", gap: 1.5 }}>
              {monthLabels.map((lbl) => (
                <Typography
                  key={lbl}
                  variant="subtitle2"
                  sx={{ height: cellSize, display: "grid", alignItems: "center" }}
                >
                  {lbl}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Columns are week-of-month index (W1..Wmax) */}
          <Box
            sx={{
              display: "grid",
              gridAutoFlow: "column",
              gridAutoColumns: `${cellSize}px`,
              gap: 0.75,
              alignItems: "start",
            }}
          >
            {weekColLabels.map((wLabel, weekIdx) => (
              <Box
                key={wLabel}
                sx={{
                  display: "grid",
                  gridTemplateRows: `22px repeat(12, ${cellSize}px)`,
                  gap: 1.5,
                }}
              >
                <Typography variant="subtitle2" sx={{ position: "sticky", top: 0, bgcolor: "background.paper" }}>
                  {t(`${tKey}.weekLabel`, { week: weekIdx + 1 })}
                </Typography>

                {months.map((m, monthIdx) => {
                  const starts = monthWeekStarts[monthIdx]
                  const weekStart = starts[weekIdx]

                  // Month doesn't have this many fixed weeks (e.g. Feb might have only 4)
                  if (!weekStart) {
                    return <Box key={`${wLabel}-${m.year}-${m.month0}`} sx={{ width: cellSize, height: cellSize }} />
                  }

                  // IMPORTANT: this computes strongest over the WHOLE 7-day week,
                  // even if it spills into adjacent months.
                  const details = computeWeekCellDetails(byDay, weekStart)
                  const strongestId = details.strongestOfWeek?.feelingId

                  const monthYear = new Date(m.year, m.month0, 1).toLocaleString(formatLocale, {
                    month: "long",
                    year: "numeric",
                  })
                  const title = t(`${tKey}.monthWeekTitle`, {
                    monthYear,
                    week: t(`${tKey}.weekLabel`, { week: weekIdx + 1 }),
                  })

                  const content = (
                    <Box>
                      <Typography variant="subtitle2" sx={{ display: "block" }}>
                        {t(`${tKey}.weekRange`, {
                          start: formatDMY(details.weekStart),
                          end: formatDMY(details.weekEnd),
                        })}
                      </Typography>

                      <Typography variant="subtitle2" sx={{ display: "block", mt: 0.5 }}>
                        {t(`${tKey}.daysWithEntries`, { count: details.daysWithEntries, total: 7 })}
                      </Typography>

                      <Typography variant="subtitle2" sx={{ display: "block", mt: 0.5 }}>
                        {details.strongestOfWeek
                          ? t(`${tKey}.strongestOfWeek`, {
                              feeling: t(optionT(details.strongestOfWeek.feelingId)),
                              sum: details.strongestOfWeek.strength,
                            })
                          : t(`${tKey}.noEntriesInWeek`)}
                      </Typography>

                      {details.topFeelings.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ display: "block" }}>
                            {t(`${tKey}.topFeelingsHeader`)}
                          </Typography>
                          {details.topFeelings.map((f) => (
                            <Stack key={f.feelingId} direction="row" spacing={1} alignItems="center">
                              <SvgIcon feelingId={f.feelingId} getSvgUrl={getSvgUrl} />
                              <Typography key={f.feelingId} variant="subtitle2" sx={{ display: "block" }}>
                                {t(`${tKey}.topFeelingLine`, {
                                  feeling: t(optionT(f.feelingId)),
                                  sum: f.sum,
                                  total: f.total,
                                })}
                              </Typography>
                            </Stack>
                          ))}
                        </Box>
                      )}

                      {details.perDayStrongest.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ display: "block" }}>
                            {t(`${tKey}.perDayStrongestHeader`)}
                          </Typography>
                          {details.perDayStrongest.slice(0, 7).map((d) => (
                            <Box key={d.isoDay}>
                              <Typography variant="subtitle2" sx={{ display: "block" }}>
                                {t(`${tKey}.perDayStrongestLine`, {
                                  date: formatDMY(new Date(d.isoDay)),
                                  feeling: t(optionT(d.feelingId)),
                                  strength: d.strength,
                                })}
                              </Typography>
                              {d.feelingTargetIds && d.feelingTargetIds.length > 0 && (
                                <Typography variant="subtitle2" sx={{ display: "block", ml: 2 }}>
                                  {d.feelingTargetIds.map((id) => t(targetT(id))).join(", ")}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )

                  return (
                    <Box
                      key={`${wLabel}-${m.year}-${m.month0}`}
                      onClick={() => {
                        openDetailsModal(title, content)
                        onDayClick?.({
                          isoDay: details.strongestOfWeek?.isoDay ?? isoDayFromDate(details.weekStart),
                          entry: details.strongestOfWeek?.isoDay
                            ? byDay.get(details.strongestOfWeek.isoDay)
                            : undefined,
                          strongestFeelingId: strongestId,
                        })
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      <SvgCell feelingId={strongestId} getSvgUrl={getSvgUrl} size={cellSize} />
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    )
  }

  // ---- Render: Month view (columns = weeks; cells = days) ----
  const renderMonthView = () => {
    const dowLabels = [
      t(`weekdays.short.mon`),
      t(`weekdays.short.tue`),
      t(`weekdays.short.wed`),
      t(`weekdays.short.thu`),
      t(`weekdays.short.fri`),
      t(`weekdays.short.sat`),
      t(`weekdays.short.sun`),
    ]

    // Rolling 4-week window ending this week (based on "today")
    const today = new Date()
    const thisWeekStart = startOfWeekMonday(today)
    const windowStart = addDays(thisWeekStart, -7 * 3) // 3 weeks before this week
    const windowEnd = endOfWeekSunday(today) // end of this week

    // Week starts for the 4 columns
    const weekStarts = [0, 1, 2, 3].map((i) => addDays(windowStart, i * 7))

    return (
      <Box sx={{ overflowX: "auto", pb: 1 }}>
        {/* Title with current date range */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t(`${tKey}.dateRange`, { start: formatDMY(windowStart), end: formatDMY(windowEnd) })}
        </Typography>

        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          {/* Day-of-week labels */}
          <Box sx={{ minWidth: 52 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateRows: `22px repeat(7, ${cellSize}px)`,
                rowGap: 0.75,
              }}
            >
              {/* Header spacer (aligns with week header row) */}
              <Box />
              {dowLabels.map((lbl) => (
                <Typography
                  key={lbl}
                  variant="subtitle2"
                  sx={{ display: "grid", alignItems: "center", height: "100%" }}
                >
                  {lbl}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* 4 columns = weeks */}
          <Box
            sx={{
              display: "grid",
              gridAutoFlow: "column",
              gridAutoColumns: `${cellSize}px`,
              gap: 1.75,
              alignItems: "start",
            }}
          >
            {weekStarts.map((ws) => {
              const we = addDays(ws, 6)
              const weekNo = getISOWeekNumber(ws)

              return (
                <Box
                  key={ws.toISOString()}
                  sx={{
                    display: "grid",
                    gridTemplateRows: `22px repeat(7, ${cellSize}px)`,
                    rowGap: 0.75,
                  }}
                >
                  {/* Column header: ISO week number */}
                  <Typography
                    variant="subtitle2"
                    sx={{ position: "sticky", top: 0, bgcolor: "background.paper" }}
                    title={`${formatDMY(ws)} .. ${formatDMY(we)}`}
                  >
                    {t(`${tKey}.weekLabel`, { week: weekNo })}
                  </Typography>

                  {/* Rows: Mon..Sun */}
                  {Array.from({ length: 7 }, (_, i) => {
                    const d = addDays(ws, i)
                    const isoDay = isoDayFromDate(d)
                    const entry = byDay.get(isoDay)
                    const strongest = strongestFeelingOfDay(entry)

                    const title = formatDMY(new Date(isoDay))
                    const content = (
                      <Box>
                        <Typography variant="subtitle2" sx={{ display: "block" }}>
                          {strongest
                            ? t(`${tKey}.strongestDay`, {
                                feeling: t(optionT(strongest.feelingId)),
                                strength: strongest.strength,
                              })
                            : t(`${tKey}.noEntry`)}
                        </Typography>

                        {entry && (entry.feelings ?? []).length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" sx={{ display: "block" }}>
                              {t(`${tKey}.allFeelingsHeader`)}
                            </Typography>
                            {[...(entry.feelings ?? [])]
                              .sort((a, b) =>
                                b.strength !== a.strength ? b.strength - a.strength : a.feelingId - b.feelingId
                              )
                              .map((f) => {
                                return (
                                  <Box key={f.feelingId}>
                                    <Stack key={f.feelingId} direction="row" spacing={0.5} alignItems="center">
                                      <Typography key={f.feelingId} variant="subtitle2" sx={{ display: "block" }}>
                                        {t(`${tKey}.feelingStrengthLine`, {
                                          feeling: t(optionT(f.feelingId)),
                                          strength: f.strength,
                                        })}
                                      </Typography>
                                      <SvgIcon feelingId={f.feelingId} getSvgUrl={getSvgUrl} />
                                    </Stack>
                                    {f.feelingTargetIds && f.feelingTargetIds.length > 0 && (
                                      <Typography variant="subtitle2" sx={{ display: "block", ml: 2 }}>
                                        {f.feelingTargetIds.map((id) => t(targetT(id))).join(", ")}
                                      </Typography>
                                    )}
                                  </Box>
                                )
                              })}
                          </Box>
                        )}
                      </Box>
                    )

                    return (
                      <Box
                        key={isoDay}
                        onClick={() => {
                          openDetailsModal(title, content)
                          onDayClick?.({
                            isoDay,
                            entry,
                            strongestFeelingId: strongest?.feelingId,
                          })
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        <SvgCell feelingId={strongest?.feelingId} getSvgUrl={getSvgUrl} size={cellSize} />
                      </Box>
                    )
                  })}
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>
    )
  }

  // ---- Render: Week view (rows = last 7 days; columns = top feelings) ----
  const renderWeekView = () => {
    // Past 7 days including today, newest -> oldest
    const end = new Date()
    end.setHours(0, 0, 0, 0)
    const start = addDays(end, -6)
    const days = Array.from({ length: 7 }, (_, i) => addDays(end, -i))

    const maxShown = 5
    const labelWidth = 54

    const getTopFeelingsForEntry = (entry?: DailyEntry) => {
      const feelings = entry?.feelings ?? []
      const sorted = [...feelings].sort((a, b) =>
        b.strength !== a.strength ? b.strength - a.strength : a.feelingId - b.feelingId
      )
      return {
        shown: sorted.slice(0, maxShown),
        overflow: Math.max(0, sorted.length - maxShown),
      }
    }

    return (
      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t(`${tKey}.dateRange`, { start: formatDMY(start), end: formatDMY(end) })}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `${labelWidth}px repeat(${maxShown + 1}, ${cellSize}px)`,
            columnGap: 0.5,
            rowGap: calendarRowGap,
            alignItems: "center",
          }}
        >
          {/* Header row */}

          {/* Day rows */}
          {days.flatMap((d) => {
            const isoDay = isoDayFromDate(d)
            const entry = byDay.get(isoDay)
            const { shown, overflow } = getTopFeelingsForEntry(entry)

            const weekday = d.toLocaleDateString(formatLocale, { weekday: "short" })

            const labelTitle = isoDay
            const labelContent = (
              <Box>
                <Typography variant="subtitle2" sx={{ display: "block" }}>
                  {entry ? t(`${tKey}.feelingsCount`, { count: (entry.feelings ?? []).length }) : t(`${tKey}.noEntry`)}
                </Typography>
                {entry && (entry.feelings ?? []).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ display: "block" }}>
                      {t(`${tKey}.allFeelingsHeader`)}
                    </Typography>
                    {[...(entry.feelings ?? [])]
                      .sort((a, b) => (b.strength !== a.strength ? b.strength - a.strength : a.feelingId - b.feelingId))
                      .map((f) => {
                        return (
                          <Box key={f.feelingId}>
                            <Stack key={f.feelingId} direction="row" spacing={0.5} alignItems="center">
                              <Typography key={f.feelingId} variant="subtitle2" sx={{ display: "block" }}>
                                {t(`${tKey}.feelingStrengthLine`, {
                                  feeling: t(optionT(f.feelingId)),
                                  strength: f.strength,
                                })}
                              </Typography>
                              <SvgIcon feelingId={f.feelingId} getSvgUrl={getSvgUrl} />
                            </Stack>
                            {f.feelingTargetIds && f.feelingTargetIds.length > 0 && (
                              <Typography variant="subtitle2" sx={{ display: "block", ml: 2 }}>
                                {f.feelingTargetIds.map((id) => t(targetT(id))).join(", ")}
                              </Typography>
                            )}
                          </Box>
                        )
                      })}
                  </Box>
                )}
              </Box>
            )

            const label = (
              <Box
                key={`lbl-${isoDay}`}
                onClick={() => openDetailsModal(labelTitle, labelContent)}
                sx={{ cursor: "pointer" }}
              >
                <Typography variant="subtitle2" sx={{ height: cellSize, display: "grid", alignItems: "center" }}>
                  {weekday}
                </Typography>
              </Box>
            )

            const feelingCells = Array.from({ length: maxShown }, (_, i) => {
              const f = shown[i]
              if (!f) {
                return (
                  <SvgCell key={`${isoDay}-empty-${i}`} feelingId={undefined} getSvgUrl={getSvgUrl} size={cellSize} />
                )
              }

              const shortDate = d.toLocaleDateString(formatLocale, {
                month: "short",
                day: "numeric",
              })
              const title = t(`${tKey}.feelingDayTitle`, {
                weekday,
                date: shortDate,
                feeling: t(optionT(f.feelingId)),
              })
              const content = (
                <Box>
                  <Stack key={f.feelingId} direction="row" spacing={0.5} alignItems="center">
                    <Typography key={f.feelingId} variant="subtitle2" sx={{ display: "block" }}>
                      {t(`${tKey}.feelingStrengthTitle`, {
                        feeling: t(optionT(f.feelingId)),
                        strength: f.strength,
                      })}
                    </Typography>
                    <SvgIcon feelingId={f.feelingId} getSvgUrl={getSvgUrl} />
                  </Stack>
                  {f.feelingTargetIds && f.feelingTargetIds.length > 0 && (
                    <Typography variant="subtitle2" sx={{ display: "block", ml: 2 }}>
                      {f.feelingTargetIds.map((id) => t(targetT(id))).join(", ")}
                    </Typography>
                  )}
                </Box>
              )

              return (
                <Box
                  key={`${isoDay}-f-${f.feelingId}-${i}`}
                  onClick={() => {
                    openDetailsModal(title, content)
                    onDayClick?.({
                      isoDay,
                      entry,
                      strongestFeelingId: f.feelingId,
                    })
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  <SvgCell feelingId={f.feelingId} getSvgUrl={getSvgUrl} size={cellSize} />
                </Box>
              )
            })

            const overflowTitle = t(`${tKey}.moreTitle`, { date: isoDay })
            const overflowContent = (
              <Box>
                <Typography variant="subtitle2" sx={{ display: "block" }}>
                  {t(`${tKey}.moreFeelings`, { count: overflow })}
                </Typography>
                {entry && (entry.feelings ?? []).length > maxShown && (
                  <Box sx={{ mt: 1 }}>
                    {[...(entry.feelings ?? [])]
                      .sort((a, b) => (b.strength !== a.strength ? b.strength - a.strength : a.feelingId - b.feelingId))
                      .slice(maxShown)
                      .map((f) => {
                        return (
                          <Box key={f.feelingId}>
                            <Stack key={f.feelingId} direction="row" spacing={0.5} alignItems="center">
                              <Typography key={f.feelingId} variant="subtitle2" sx={{ display: "block" }}>
                                {t(`${tKey}.feelingStrengthTitle`, {
                                  feeling: t(optionT(f.feelingId)),
                                  strength: f.strength,
                                })}
                              </Typography>
                              <SvgIcon feelingId={f.feelingId} getSvgUrl={getSvgUrl} />
                            </Stack>
                            {f.feelingTargetIds && f.feelingTargetIds.length > 0 && (
                              <Typography variant="subtitle2" sx={{ display: "block", ml: 2 }}>
                                {f.feelingTargetIds.map((id) => t(targetT(id))).join(", ")}
                              </Typography>
                            )}
                          </Box>
                        )
                      })}
                  </Box>
                )}
              </Box>
            )

            const overflowCell =
              overflow > 0 ? (
                <Box
                  key={`${isoDay}-overflow`}
                  onClick={() => openDetailsModal(overflowTitle, overflowContent)}
                  sx={{
                    width: cellSize,
                    height: cellSize,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="subtitle2">+{overflow}</Typography>
                </Box>
              ) : (
                <Box
                  key={`${isoDay}-overflow-empty`}
                  sx={{
                    width: cellSize,
                    height: cellSize,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                />
              )

            return [label, ...feelingCells, overflowCell]
          })}
        </Box>
      </Box>
    )
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">{t(`${tKey}.title`)}</Typography>
          <IconButton
            aria-label={t(`${tKey}.help`)}
            onClick={() =>
              openDetailsModal(
                t(`${tKey}.helpTitle`),
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t(`${tKey}.helpContent1`)}
                  </Typography>
                </Box>
              )
            }
          >
            <InfoIcon color="info" />
          </IconButton>
        </Stack>

        {view === "year" ? renderYearView() : view === "week" ? renderWeekView() : renderMonthView()}

        <Dialog open={detailsModal.open} onClose={closeDetailsModal} fullWidth maxWidth="xs">
          <DialogTitle sx={{ pr: 6 }}>
            {detailsModal.title}
            <IconButton
              aria-label={t(`${tKey}.close`)}
              onClick={closeDetailsModal}
              sx={{ position: "absolute", right: 8, top: 8, color: "error.main" }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>{detailsModal.content}</DialogContent>
          <DialogActions>
            <Button onClick={closeDetailsModal}>{t(`${tKey}.close`)}</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  )
}
